import type { GuidanceNotice } from '#shared/guidance/guidanceArticle'

/**
 * Above this many characters a notice is collapsed to a couple of lines behind a "show more".
 *
 * A character count rather than a measured overflow on purpose: the banner sits above the whole app
 * shell, so the decision has to be made on the first render, before layout, or a long notice pushes
 * every /app route down by a paragraph and then snaps back. It is a threshold for *whether to offer
 * the toggle*, not for what gets rendered — the clamp itself is CSS, so a short-but-tall notice
 * (many hard line breaks) still clamps; it just does not advertise the control.
 */
export const NOTICE_CLAMP_CHARS = 180

/** Whether a notice's body earns a show-more/show-less toggle. */
export function isLongNotice(content: string | null | undefined): boolean {
  return (content ?? '').length > NOTICE_CLAMP_CHARS
}

/**
 * The org's active notices, and the per-user dismissal that removes one.
 *
 * A notice is a guidance article an admin marked `is_notice` and left `notice_active`. The server
 * already answers the only question worth asking — `GET /api/guidance/active-notices` returns the
 * notices for THIS org that THIS user has not dismissed, newest first — so there is nothing to
 * filter here. Dismissal is per user (`guidance_dismissals`), never per org: one member closing a
 * notice must not take it off everybody else's screen.
 *
 * **Failure is silence.** The banner hangs off the app shell, above every /app route, and a notice
 * is an announcement rather than a workflow step. If the fetch fails there is nothing to say that
 * is worth a red box across the top of the app, so `notices` stays empty and the banner does not
 * render — the same trade `welcome/RecentActivity.vue` makes for the same reason.
 *
 * **Dismissal is optimistic, and rolls back.** The row disappears on click rather than after a
 * round trip, because waiting on the network to close a banner reads as a broken button. If the
 * POST then fails the notice comes back, which is the honest outcome: the dismissal was not
 * recorded, so it would return on the next load anyway, and showing it now beats showing it
 * tomorrow with no explanation.
 */
export function useGuidanceNotices() {
  const { apiFetch } = useApi()

  const notices = ref<GuidanceNotice[]>([])
  /** Distinguishes "not fetched yet" from "fetched, nothing active" — the banner renders on neither. */
  const loaded = ref(false)

  async function load() {
    try {
      notices.value = (await apiFetch<GuidanceNotice[]>('/api/guidance/active-notices')) ?? []
    } catch {
      notices.value = []
    } finally {
      loaded.value = true
    }
  }

  async function dismiss(id: string) {
    const previous = notices.value
    if (!previous.some(n => n.id === id)) return
    notices.value = previous.filter(n => n.id !== id)
    try {
      await apiFetch(`/api/guidance/${id}/dismiss`, { method: 'POST' })
    } catch {
      // Put it back in its original position — restoring the array wholesale rather than pushing,
      // so a rolled-back dismissal does not silently reorder the stack.
      notices.value = previous
    }
  }

  return { notices, loaded, load, dismiss }
}
